import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Database, MapPin, Calendar, Sparkles, ExternalLink, Trash2, KeyRound } from "lucide-react";
import { PasswordInput } from "../../components/PasswordInput";
import {
  AdminSecurityCard,
  LoginJournalCard,
} from "./AdminSecurityCard";

export const AdminSettings = () => {
  const [config, setConfig] = useState(null);
  const [notionCheck, setNotionCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    testNotion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testNotion = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/config/notion-check");
      setNotionCheck(data);
    } catch {
      toast.error("Impossible de contacter Notion");
    } finally {
      setChecking(false);
    }
  };

  const runEnrich = async () => {
    setEnriching(true);
    const t = toast.loading("Enrichissement…");
    try {
      await api.post("/sync/enrich");
      toast.success("Enrichissement lancé en tâche de fond", {
        id: t,
        description: "Ça peut prendre 2 à 5 min. Rafraîchis la page /sorties ensuite.",
      });
    } catch (e) {
      toast.error("Erreur", { id: t, description: e?.message });
    } finally {
      setEnriching(false);
    }
  };

  const resetFilms = async () => {
    if (!window.confirm(
      "Supprimer TOUS les films de la base ?\n\n" +
      "Cela vide la table Films en base (Notion reste intacte). " +
      "Ensuite lance une sync depuis le tableau de bord pour repartir sur " +
      "les vraies sorties cinéma (release_type 2|3).\n\nContinuer ?"
    )) return;
    setResetting(true);
    const t = toast.loading("Suppression des films…");
    try {
      const { data } = await api.post("/sync/reset-films");
      toast.success(`${data.deleted} films supprimés`, {
        id: t,
        description: "Va sur le tableau de bord et clique « Lancer la synchro ».",
      });
    } catch (e) {
      toast.error("Erreur", { id: t, description: e?.message });
    } finally {
      setResetting(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe");
      return;
    }
    if (pwdForm.next.length < 10) {
      toast.error("Le nouveau mot de passe doit faire au moins 10 caractères");
      return;
    }
    setPwdBusy(true);
    const t = toast.loading("Mise à jour du mot de passe…");
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current,
        new_password: pwdForm.next,
      });
      setPwdForm({ current: "", next: "", confirm: "" });
      toast.success("Mot de passe mis à jour", {
        id: t,
        description: "Ta session actuelle reste valide. Le nouveau mot de passe est actif dès maintenant.",
      });
    } catch (err) {
      toast.error("Impossible de changer le mot de passe", {
        id: t,
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setPwdBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Automatisation</div>
        <h1 className="font-display text-4xl mt-2">Réglages</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="Connexions" icon={Database}>
          <Row label="TMDB API" ok={!!config?.tmdb_configured} value={config?.tmdb_configured ? "Clé chargée" : "Non configurée"} />
          <Row label="Notion" ok={!!config?.notion_configured} value={config?.notion_configured ? "Token chargé" : "Non configurée"} />
          <Row label="Base Notion" ok={!!config?.notion_database_id_masked} value={config?.notion_database_id_masked || "—"} mono />
          <div className="pt-2 flex flex-wrap gap-2">
            <button onClick={testNotion} disabled={checking} className="btn-ghost text-xs" data-testid="test-notion-btn">
              {checking ? "Test…" : "Tester Notion"}
            </button>
            <button onClick={runEnrich} disabled={enriching} className="btn-ghost text-xs" data-testid="enrich-btn">
              <Sparkles className="h-3.5 w-3.5" />
              {enriching ? "Enrichissement…" : "Enrichir Genre + Pays"}
            </button>
            <button
              onClick={resetFilms}
              disabled={resetting}
              className="btn-ghost text-xs border-[var(--red)] text-[var(--red)] hover:bg-[var(--red-soft)]"
              data-testid="reset-films-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {resetting ? "Suppression…" : "Réinitialiser les films"}
            </button>
          </div>
          {notionCheck?.ok && (
            <div className="text-xs text-emerald-700 mt-3 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Base « {notionCheck.database_title} » accessible.
            </div>
          )}
          {notionCheck && !notionCheck.ok && (
            <div className="text-xs text-[var(--red)] mt-3 flex items-start gap-1">
              <XCircle className="h-3.5 w-3.5 mt-0.5" /> {notionCheck.error}
            </div>
          )}
        </Card>

        <Card title="Périmètre" icon={MapPin}>
          <Row label="Région" value={config?.region} />
          <Row label="Langue" value={config?.language} />
          <Row label="Fenêtre" value={config ? `${config.window_days} jours` : "—"} />
          <div className="pt-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Plateformes suivies</div>
            <div className="flex flex-wrap gap-1.5">
              {(config?.target_platforms || []).map((p) => (
                <span key={p} className="text-xs px-2 py-1 border border-[var(--line-strong)] rounded">{p}</span>
              ))}
            </div>
          </div>
        </Card>

        <Card title="Planification" icon={Calendar}>
          <Row label="Fréquence" value="Quotidienne" />
          <Row label="Heure" value={`${config?.schedule_utc || "—"} UTC`} />
          <p className="text-xs text-neutral-500 mt-2">
            L'exécution automatique se lance à cette heure chaque jour. Vous pouvez déclencher
            manuellement depuis le tableau de bord.
          </p>
        </Card>

        <Card title="Mapping Notion" icon={Database}>
          {config && Object.entries(config.notion_props).map(([k, v]) => (
            <Row key={k} label={({ name: "Nom", platform: "Plateforme", date: "Date de sortie", type: "Type" }[k])} value={v} mono />
          ))}
          <Row label="Genre (enrichi)" value="Genre" mono />
          <Row label="Pays (enrichi)" value="Pays" mono />
          <a href="https://www.notion.so/my-integrations" target="_blank" rel="noreferrer" className="text-xs text-[var(--red)] mt-3 inline-flex items-center gap-1">
            Intégrations Notion <ExternalLink className="h-3 w-3" />
          </a>
        </Card>

        <Card title="Sécurité" icon={KeyRound}>
          <p className="text-xs text-neutral-500 mb-3">
            Change ton mot de passe admin. Minimum 10 caractères. Ta session actuelle
            reste valide, mais toute nouvelle connexion devra utiliser le nouveau mot de passe.
          </p>
          <form onSubmit={changePassword} className="space-y-3">
            <PwdField
              testId="pwd-current"
              label="Mot de passe actuel"
              value={pwdForm.current}
              onChange={(v) => setPwdForm((f) => ({ ...f, current: v }))}
              autoComplete="current-password"
            />
            <PwdField
              testId="pwd-next"
              label="Nouveau mot de passe"
              value={pwdForm.next}
              onChange={(v) => setPwdForm((f) => ({ ...f, next: v }))}
              autoComplete="new-password"
            />
            <PwdField
              testId="pwd-confirm"
              label="Confirmer"
              value={pwdForm.confirm}
              onChange={(v) => setPwdForm((f) => ({ ...f, confirm: v }))}
              autoComplete="new-password"
            />
            <button
              type="submit"
              disabled={pwdBusy || !pwdForm.current || !pwdForm.next || !pwdForm.confirm}
              data-testid="pwd-submit"
              className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-2 bg-black text-white rounded-sm hover:bg-[var(--red)] transition-colors disabled:opacity-50"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {pwdBusy ? "Mise à jour…" : "Mettre à jour"}
            </button>
          </form>
        </Card>
      </div>

      <AdminSecurityCard />
      <LoginJournalCard />
    </div>
  );
};

const PwdField = ({ label, value, onChange, testId, autoComplete }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
    <PasswordInput
      testId={testId}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
    />
  </label>
);

const Card = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-[var(--line)] rounded-md p-6">
    <div className="flex items-center gap-2 mb-4">
      <div className="h-8 w-8 grid place-items-center rounded bg-[var(--red-soft)] text-[var(--red)]">
        <Icon className="h-4 w-4" />
      </div>
      <h2 className="font-display text-lg">{title}</h2>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ label, ok, value, mono }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-[var(--line)] last:border-b-0 text-sm">
    <div className="text-neutral-500">{label}</div>
    <div className="flex items-center gap-2">
      <span className={mono ? "font-mono text-xs" : ""}>{value}</span>
      {ok === true && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
      {ok === false && <XCircle className="h-3.5 w-3.5 text-[var(--red)]" />}
    </div>
  </div>
);
